import React, { FC, ReactNode, memo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import Stack, { StackProps } from '@mui/material/Stack';
import { GridRowId } from '@mui/x-data-grid-pro';
import isEqual from 'lodash/isEqual';

import { getDragDropGridIdField, getDroppableId } from './DataGrid.utils';

interface Props extends Omit<StackProps, 'id'> {
  id: GridRowId;
  field: string;
  droppingSx?: StackProps['sx'];
  activeSx?: StackProps['sx'];
  disabled?: boolean;
  children?: ReactNode;
}

export const DataGridDropCellWrapper: FC<Props> = memo(
  ({ id, field, droppingSx, activeSx, disabled, sx, children, ...props }) => {
    // Droppable
    const {
      isOver,
      setNodeRef: setDroppableNodeRef,
      active,
      over,
    } = useDroppable({ id: getDroppableId(id, field), disabled });

    // True if draggable cell is over its associated droppable cell; false otherwise
    const isAtOrigin = !active || !over || isEqual(getDragDropGridIdField(active), getDragDropGridIdField(over));

    const droppableSx = isOver && !isAtOrigin ? ({ ...sx, ...droppingSx } as StackProps['sx']) : sx;

    // True if dragging cell has same same id, field
    const hasActive = active && isEqual(getDragDropGridIdField(active), { id, field });

    const hasActiveSx = hasActive ? { ...activeSx } : {};

    const combinedSx = { ...droppableSx, ...hasActiveSx } as StackProps['sx'];

    return (
      <Stack
        ref={setDroppableNodeRef}
        alignItems="center"
        height="100%"
        width="100%"
        flexGrow={1}
        overflow="hidden"
        sx={combinedSx}
        {...props}
      >
        {children}
      </Stack>
    );
  },
);
